import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  NewUsersResponse,
  ActiveUsersResponse,
  UniqueSendersResponse,
  ReferralTrafficResponse,
  CardsAddedResponse,
  PointsInEscrowResponse,
  CardsInTransitResponse,
  PointsCreatedResponse,
  PointsDestroyedResponse,
  EconomicBalanceResponse,
  EscrowRatioResponse,
  TotalPointsResponse,
  PointsPerUserResponse,
  TradesPerDayResponse,
  ActivePaidEliteResponse,
  EliteFlowResponse,
  SmsAlertsResponse,
  AvgCardValueResponse,
  InitiatedTradeValueResponse,
  AnalyticsPeriod,
  CardAddSourcesResponse,
  TradeInitiationSourcesResponse,
  TrialFlowResponse,
  SmsAlertsByUserResponse,
  FomoSmsResponse,
  OnboardingFunnelResponse,
  TradeOffersByStatusResponse,
  TradeOffersByUserResponse
} from 'types/interfaces/admin/analytics';

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  baseQuery: baseQuery,
  tagTypes: ['Analytics'],
  endpoints: (builder) => ({
    getNewUsers: builder.query<NewUsersResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/new-users?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getActiveUsers: builder.query<ActiveUsersResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/active-users?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getUniqueSenders: builder.query<UniqueSendersResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/unique-senders?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getReferralTraffic: builder.query<ReferralTrafficResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/referral-traffic?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getCardsAdded: builder.query<CardsAddedResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/cards-added?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getPointsInEscrow: builder.query<PointsInEscrowResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/points-in-escrow?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getCardsInTransit: builder.query<CardsInTransitResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/cards-in-transit?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getPointsCreated: builder.query<PointsCreatedResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/points-created?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getPointsDestroyed: builder.query<PointsDestroyedResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/points-destroyed?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getEconomicBalance: builder.query<EconomicBalanceResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/economic-balance?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getEscrowRatio: builder.query<EscrowRatioResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/escrow-ratio?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getTotalPoints: builder.query<TotalPointsResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/total-points?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getPointsPerUser: builder.query<PointsPerUserResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/points-per-user?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getTradesPerDay: builder.query<TradesPerDayResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/trades-per-day?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getActivePaidElite: builder.query<ActivePaidEliteResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/active-paid-elite?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getEliteFlow: builder.query<EliteFlowResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/elite-flow?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getSmsAlertsPerDay: builder.query<SmsAlertsResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/sms-alerts-per-day?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getAvgCardValuePerDay: builder.query<AvgCardValueResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/avg-card-value-per-day?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getInitiatedTradeValuePerDay: builder.query<InitiatedTradeValueResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/analytics/initiated-trade-value-per-day?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    // --- Telemetry chart batch (/admin/charts/*) ---
    getCardAddSources: builder.query<CardAddSourcesResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/card-add-sources?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getTradeInitiationSources: builder.query<TradeInitiationSourcesResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/trade-initiation-sources?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getTrialFlow: builder.query<TrialFlowResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/trial-signups-cancellations?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getSmsAlertsByUser: builder.query<SmsAlertsByUserResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/sms-alerts-by-user?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getFomoSmsVolume: builder.query<FomoSmsResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/fomo-sms-volume?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getOnboardingFunnel: builder.query<OnboardingFunnelResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/onboarding-funnel?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getTradeOffersByStatus: builder.query<TradeOffersByStatusResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/trade-offers-by-status?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
    getTradeOffersByUser: builder.query<TradeOffersByUserResponse, { period: AnalyticsPeriod }>({
      query: ({ period }) => ({
        url: `/admin/charts/trade-offers-by-user?period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Analytics'],
    }),
  }),
});

export const {
  useGetNewUsersQuery,
  useGetActiveUsersQuery,
  useGetUniqueSendersQuery,
  useGetReferralTrafficQuery,
  useGetCardsAddedQuery,
  useGetPointsInEscrowQuery,
  useGetCardsInTransitQuery,
  useGetPointsCreatedQuery,
  useGetPointsDestroyedQuery,
  useGetEconomicBalanceQuery,
  useGetEscrowRatioQuery,
  useGetTotalPointsQuery,
  useGetPointsPerUserQuery,
  useGetTradesPerDayQuery,
  useGetActivePaidEliteQuery,
  useGetEliteFlowQuery,
  useGetSmsAlertsPerDayQuery,
  useGetAvgCardValuePerDayQuery,
  useGetInitiatedTradeValuePerDayQuery,
  useGetCardAddSourcesQuery,
  useGetTradeInitiationSourcesQuery,
  useGetTrialFlowQuery,
  useGetSmsAlertsByUserQuery,
  useGetFomoSmsVolumeQuery,
  useGetOnboardingFunnelQuery,
  useGetTradeOffersByStatusQuery,
  useGetTradeOffersByUserQuery
} = analyticsApi;
